import React, { useState, useContext, useMemo } from "react";
import { Header } from "us.collection.creditor/layouts/CreditorView/Header";
import { matchPath, useLocation, useNavigate, Outlet } from "react-router-dom";
import { BoxIcons, IconTypes } from "us.icons";
import { useTranslation } from "react-i18next";
import Common from "us.common";
import { ConfigurationContext } from "us.common/ConfigurationContext/ConfigurationProvider";
import { getSelectedMenuKeyByModule } from "us.collection/functions";
import { INavigationData } from "us.collection/interfaces";

const { $Layout, $Menu } = Common.Components;

const { Content, Sider } = $Layout;

interface IHomeProps {
  children?: React.ReactNode;
}

const Home: React.FC<IHomeProps> = (props) => {
  const { t } = useTranslation(["US.COLLECTION.LAYOUT"]);
  const context = useContext(ConfigurationContext);

  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const state = location.state as INavigationData;

  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);

  const { params }: any = matchPath(
    {
      path: "/:type/:Id?/:module?",
      end: false,
      caseSensitive: false,
    },
    pathname
  );

  const creditorPermission = useMemo(() => {
    return context.componentPermission["creditor"];
  }, [context]);

  const navigateTo = (url: string): void => {
    console.log("Navigating to:", url, state);
    window.scrollTo(0, 0);
    const match = matchPath(
      { path: "/creditor/:Id/:module?", end: false, caseSensitive: false },
      pathname
    );
    const id = match?.params?.Id;
    if (id) {
      navigate(
        {
          ...location,
          pathname: `/creditor/${id}/${url}`,
        },
        { state }
      );
    }
  };

  const onCollapse = (collapsed: boolean): void => {
    setIsCollapsed(collapsed);
  };

  const menuFieldsAvalibility = (menuItem: string): boolean => {
    return creditorPermission?.routes[menuItem]?.isEnabled;
  };

  return (
    <$Layout style={{ minHeight: "100vh" }}>
      <Header {...props} />
      <$Layout>
        <Content className="m-0">
          <Outlet />
        </Content>
        <Sider
          collapsible
          collapsed={isCollapsed}
          onCollapse={(e) => onCollapse(e)}
          reverseArrow={true}
        >
          <$Menu
            theme="dark"
            mode="inline"
            selectedKeys={getSelectedMenuKeyByModule(params["module"])}
          >
            <$Menu.Item onClick={() => navigateTo("creditor-cases")} key="2">
              <BoxIcons type={IconTypes.BOX_ICON} name="creditor-cases" />
              <span>{t("US.COLLECTION.LAYOUT:CASE.CREDITOR_CASES")}</span>
            </$Menu.Item>
            {menuFieldsAvalibility("caseDocuments") && (
              <$Menu.Item
                onClick={() => navigateTo("documents")}
                key="4"
                data-testid="creditor-layout-caseDocuments"
              >
                <BoxIcons type={IconTypes.BOX_ICON} name="documents" />
                <span>{t("US.COLLECTION.LAYOUT:CASE.CREDITOR_DOCUMENTS")}</span>
              </$Menu.Item>
            )}
            {menuFieldsAvalibility("routinesAndActivities") && (
              <$Menu.Item
                onClick={() => navigateTo("history")}
                key="16"
                data-testid="creditor-layout-routinesAndActivities"
              >
                <BoxIcons type={IconTypes.BOX_ICON} name="activity-history" />
                <span> {t("US.COLLECTION.LAYOUT:CASE.HISTORY")}</span>
              </$Menu.Item>
            )}
            {menuFieldsAvalibility("otherParty") && (
              <$Menu.Item
                onClick={() => navigateTo("other-party")}
                key="14"
                data-testid="creditor-layout-otherParty"
              >
                <BoxIcons type={IconTypes.BOX_ICON} name="other-party" />
                <span>{t("US.COLLECTION.LAYOUT:CASE.OTHERPARTY")}</span>
              </$Menu.Item>
            )}
            {menuFieldsAvalibility("notes") && (
              <$Menu.Item
                onClick={() => navigateTo("notes")}
                key="15"
                data-testid="creditor-layout-notes"
              >
                <BoxIcons type={IconTypes.BOX_ICON} name="note" />
                <span>{t("US.COLLECTION.LAYOUT:CASE.NOTES")}</span>
              </$Menu.Item>
            )}
            {menuFieldsAvalibility("bmd") && (
              <$Menu.Item
                key="17"
                onClick={() => navigateTo("bmd")}
                data-testid="creditor-layout-bmd"
              >
                <BoxIcons type={IconTypes.BOX_ICON} name="bmd" />
                <span>{t("US.COLLECTION.LAYOUT:CASE.BMD")}</span>
              </$Menu.Item>
            )}
            <$Menu.Item
              key="18"
              onClick={() => navigateTo("creditor-information")}
            >
              <BoxIcons type={IconTypes.BOX_ICON} name="creditor-information" />
              <span>
                {t("US.COLLECTION.LAYOUT:CREDITOR.CREDITOR_INFORMATION")}
              </span>
            </$Menu.Item>
            <$Menu.Item key="19" onClick={() => navigateTo("workflows")}>
              <BoxIcons type={IconTypes.BOX_ICON} name="bureau-workflows" />
              <span>{t("US.COLLECTION.LAYOUT:CREDITOR.WORKFLOWS")}</span>
            </$Menu.Item>
            <$Menu.Item
              key="20"
              onClick={() => navigateTo("workflows-troubleshoot")}
            >
              <BoxIcons
                type={IconTypes.BOX_ICON}
                name="workflows-troubleshoot"
              />
              <span>
                {t("US.COLLECTION.LAYOUT:BUREAU.WORKFLOWS_TROUBLESHOOT")}
              </span>
            </$Menu.Item>
          </$Menu>
        </Sider>
      </$Layout>
    </$Layout>
  );
};

export default Home;
